import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Spin } from "antd";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import "katex/dist/katex.min.css";
import styles from "./index.module.less";
import cx from "classnames";
import wsuiApi from "@api/wsuiApi";
import type { IContentItem } from "@typing/wsui/referenceContent";
import {
    isMarkdownFormat,
    isMarkdownFormatData,
    TocItem,
    parseMarkdownToc,
    generateTocFromContent,
    processMarkdownWithAnchors,
} from "./utils";

let pdfIcon = "https://muzhi-public-pic.cdn.bcebos.com/100115354305";
let arrowIcon = "https://muzhi-public-pic.cdn.bcebos.com/100115354312";
/**
 * Markdown内容渲染组件
 */
interface MarkdownContentRendererProps {
    markdownContent: string;
    pageTitle: string;
    approvalDate: IContentItem;
    tocData: TocItem[];
}

const MarkdownContentRenderer: React.FC<MarkdownContentRendererProps> = ({
    markdownContent,
    pageTitle,
    approvalDate,
    tocData,
}) => {
    const processedContent = processMarkdownWithAnchors(markdownContent, tocData);

    return (
        <div className={styles.markdownFullContent}>
            <div className={styles.markdownHeader}>
                <h1>{pageTitle}</h1>
                {approvalDate.content && <div className={styles.approvalDate}>核准日期：{approvalDate.content}</div>}
            </div>

            <div className={styles.markdownBody}>
                <ReactMarkdown
                    remarkPlugins={[
                        [remarkGfm, { singleTilde: false }], // 禁用单波浪线删除线，只支持 ~~text~~
                        remarkBreaks,
                        remarkMath,
                    ]}
                    rehypePlugins={[rehypeKatex, rehypeRaw]}
                >
                    {processedContent}
                </ReactMarkdown>
            </div>
        </div>
    );
};

/**
 * 药品说明书预览页面
 * 展示引用内容的详细信息（支持嵌套结构）
 */
const formatContent = (text: string): string =>
    text
        .replace(/\\\./g, '.')   // \. → .  去掉多余转义反斜杠
        .replace(/\n{2,}/g, '\n'); // 多个连续换行 → 单个换行

const DrugManualPreview: React.FC = () => {
    // 从路由获取参数
    const {
        type,
        id,
        title: urlTitle,
    } = useParams<{
        type: string;
        id: string;
        title?: string;
    }>();

    // 数据状态
    const [loading, setLoading] = useState<boolean>(true);
    const [pageTitle, setPageTitle] = useState<string>("");
    const [contentList, setContentList] = useState<IContentItem[]>([]);
    const [approvalDate, setApprovalDate] = useState<IContentItem>({
        key: "approvalDate",
        key_desc: "核准日期",
        content: "",
    });
    const [error, setError] = useState<string>("");

    // 当前激活的目录项
    const [activeId, setActiveId] = useState<string>("");

    // 滚动容器 ref，供滚动监听使用
    const contentWrapperRef = useRef<HTMLDivElement>(null);
    // 吸顶标题 ref，用于获取标题高度补偿
    const stickyTitleRef = useRef<HTMLDivElement>(null);

    // 是否为markdown格式数据
    const isMarkdownData = useMemo(() => {
        return isMarkdownFormatData({ content_list: contentList });
    }, [contentList]);

    // 生成目录数据 - 支持两种格式
    const tocData = useMemo(() => {
        if (isMarkdownData && contentList[0]) {
            return parseMarkdownToc(contentList[0].content);
        }
        return generateTocFromContent(contentList);
    }, [contentList, isMarkdownData]);

    // 获取数据
    useEffect(() => {
        const fetchData = async () => {
            if (!type || !id) {
                setError("缺少必要参数");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");

            try {
                const response = await wsuiApi.getReferenceContent({
                    type,
                    id,
                    title: urlTitle || "",
                    businessType: 'doctor_youyi',
                });

                if (response.errno === 0 && response.data) {
                    setPageTitle(response.data.title || urlTitle || "详情");
                    setApprovalDate(
                        response.data?.content_list?.filter((item) => item.key === "approvalDate")?.[0] || {}
                    );
                    setContentList(response.data?.content_list?.filter((item) => item.key !== "approvalDate") || []);
                    // 设置第一个内容项为激活状态
                    if (response.data.content_list?.length > 0) {
                        setActiveId(response.data.content_list[0].key);
                    }
                } else {
                    setError(response.errmsg || "获取数据失败");
                }
            } catch (err) {
                console.error("获取引用内容失败:", err);
                setError("网络请求失败，请稍后重试");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [type, id, urlTitle]);

    // 滚动同步目录高亮
    useEffect(() => {
        if (!tocData.length || !contentWrapperRef.current) return;

        // 收集 level 1、2 的 id（按文档顺序）
        const getAllIds = (items: TocItem[]): string[] =>
            items.flatMap((item) => {
                if (item.level === 3) return [];
                return [item.id, ...getAllIds(item.children || [])];
            });

        const ids = getAllIds(tocData);
        const wrapper = contentWrapperRef.current;

        // 缓存标题高度，避免每次 scroll 都查 DOM
        const titleHeight = stickyTitleRef.current?.offsetHeight ?? 0;

        // 点击目录滚动时暂停监听，避免滚动期间 activeId 抖动
        let isScrollingByClick = false;

        const updateActive = () => {
            if (isScrollingByClick) return;

            const wrapperTop = wrapper.getBoundingClientRect().top;

            // 找最后一个标题已滚过基准线的 section（标题+内容都过了才切下一个）
            let lastAboveId: string | null = null;

            for (const id of ids) {
                const el = document.getElementById(id);
                if (!el) continue;
                const elTop = el.getBoundingClientRect().top - wrapperTop;
                if (elTop < titleHeight) {
                    // 标题已滚过基准线，记录为候选
                    lastAboveId = id;
                } else {
                    // 第一个还在基准线下方的 section
                    if (!lastAboveId) {
                        // 还没有任何 section 滚过基准线，激活当前（页面顶部状态）
                        setActiveId(id);
                        return;
                    }
                    // lastAboveId 的内容仍在基准线下方，保持它高亮
                    break;
                }
            }

            if (lastAboveId) {
                setActiveId(lastAboveId);
            }
        };

        wrapper.addEventListener("scroll", updateActive, { passive: true });
        updateActive();

        return () => wrapper.removeEventListener("scroll", updateActive);
    }, [tocData]);

    // 滚动到指定锚点
    const scrollToSection = useCallback((sectionId: string) => {
        setActiveId(sectionId);
        const wrapper = contentWrapperRef.current;
        const element = document.getElementById(sectionId);
        if (wrapper && element) {
            const wrapperTop = wrapper.getBoundingClientRect().top;
            
            const titleHeight = stickyTitleRef.current?.offsetHeight ?? 0;
            // 元素在容器可见区域内的位置 → 完整内容中的绝对位置 → 减去标题高度
            const elAbsoluteY = element.getBoundingClientRect().top - wrapperTop + wrapper.scrollTop;
            const targetTop = elAbsoluteY - titleHeight;
            wrapper.scrollTo({ top: targetTop, behavior: "smooth" });
        }
    }, []);

    // 递归渲染内容项
    const renderContentItem = useCallback((item: IContentItem, level: number = 1): React.ReactNode => {
        const hasChildren = item.children && item.children.length > 0;
        const hasContent = item.content && item.content.trim() !== "";
        // 检测内容是否为Markdown格式
        const isMarkdown = hasContent ? isMarkdownFormat(item.content) : false;

        if (!hasContent && !hasChildren) return null;

        // 根据层级选择不同的样式类名
        const getLabelClassName = () => {
            if (level === 1) return styles.infoLabel;
            if (level === 2) return styles.subGroupTitle;
            return styles.subItemTitle;
        };

        // 判断是否为 originalPdf 类型
        const isOriginalPdf = item.key === "originalPdf";

        // originalPdf 类型使用特殊的 PDF 卡片结构
        if (isOriginalPdf && hasContent) {
            // 解析 content，支持 JSON 格式（{name, size, url}）或纯文本
            let pdfName = item.key_desc || "说明书原件";
            let pdfSize = "";
            let pdfUrl = item.content;

            try {
                const pdfData = JSON.parse(item.content);
                pdfName = pdfData.name || pdfName;
                pdfSize = pdfData.size || "";
                pdfUrl = pdfData.url || item.content;
            } catch {
                // content 不是 JSON，作为 URL 使用
            }

            return (
                <div key={item.key} id={item.key} className={styles.originalPdfItem}>
                    <div className={styles.infoLabel}>{item.key_desc}</div>
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className={styles.pdfCard}>
                        <div className={styles.pdfIcon}>
                            <img src={pdfIcon} alt="PDF" />
                        </div>
                        <div className={styles.pdfInfo}>
                            <span className={styles.pdfName}>{pdfName}</span>
                            <div className={styles.pdfMeta}>
                                <span>PDF</span>
                                {pdfSize && <span>{pdfSize}</span>}
                            </div>
                        </div>
                        <div className={styles.pdfArrow}>
                            <img src={arrowIcon} alt="打开" />
                        </div>
                    </a>
                </div>
            );
        }

        return (
            <div key={item.key} id={item.key} className={cx(level === 1 ? styles.infoItem : styles.subItem)}>
                <div className={getLabelClassName()}>{item.key_desc}</div>
                {hasContent && (
                    <div className={cx(styles.markdownContent, level <= 2 ? styles.infoValue : styles.subItemContent)}>
                        {isMarkdown ? (
                            <ReactMarkdown
                                remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkBreaks, remarkMath]}
                                rehypePlugins={[rehypeKatex, rehypeRaw]}
                            >
                                {item.content}
                            </ReactMarkdown>
                        ) : (
                            <div className={styles.commonText}>{formatContent(item.content)}</div>
                        )}
                    </div>
                )}
                {hasChildren && (
                    <div className={level === 1 ? styles.sectionContent : styles.subGroupContent}>
                        {item.children!.map((child) => renderContentItem(child, level + 1))}
                    </div>
                )}
            </div>
        );
    }, []);

    // 树形结构内容渲染
    const renderTreeContent = (): React.ReactNode => {
        return (
            <div className={styles.sectionContentWrapper}>{contentList.map((item) => renderContentItem(item, 1))}</div>
        );
    };

    // 递归渲染目录项 - 重构为分层结构
    const renderTocItems = useCallback(
        (items: TocItem[]): React.ReactNode => {
            return items.map((item) => {
                // 三级目录不在目录中展示
                if (item.level === 3) return null;

                const hasChildren = item.children && item.children.length > 0;

                // 根级容器和子级容器使用不同的样式
                const getContainerClassName = () => {
                    if (item.level === 1) return styles.tocLevel1;
                    if (item.level === 2) return styles.tocLevel2;
                    return styles.tocLevel3;
                };

                const getItemClassName = () => {
                    if (item.level === 1) return styles.tocItem;
                    if (item.level === 2) return styles.tocSubItem;
                    return styles.tocSubSubItem;
                };

                return (
                    <div key={item.id} className={getContainerClassName()}>
                        <div
                            className={cx(getItemClassName(), {
                                [styles.active]: activeId === item.id,
                            })}
                            onClick={() => scrollToSection(item.id)}
                        >
                            {item.label}
                        </div>
                        {hasChildren && <div className={styles.tocChildren}>{renderTocItems(item.children!)}</div>}
                    </div>
                );
            });
        },
        [activeId, scrollToSection]
    );

    // 渲染 Header 组件（避免重复代码）
    const renderHeader = (title: string) => (
        <header className={styles.header}>
            <div className={styles.logo}>
                <img src="https://muzhi-public-pic.cdn.bcebos.com/100115321291" alt="百度健康" />
            </div>
            <div className={styles.divider} />
            <span className={styles.pageTitle}>药品说明书</span>
        </header>
    );

    // Loading 状态
    if (loading) {
        return (
            <div className={styles.pageContainer}>
                {renderHeader(urlTitle || "加载中...")}
                <main className={styles.mainContent}>
                    <div className={styles.loadingWrapper}>
                        <Spin size="large" />
                    </div>
                </main>
            </div>
        );
    }

    // 错误状态
    if (error) {
        return (
            <div className={styles.pageContainer}>
                {renderHeader("出错了")}
                <main className={styles.mainContent}>
                    <div className={styles.errorWrapper}>
                        <p>{error}</p>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* 顶部导航栏 */}
            {renderHeader(pageTitle)}

            {/* 主体内容 */}
            <main className={styles.mainContent}>
                <div className={styles.contentWrapper} ref={contentWrapperRef}>
                    {/* 左侧文档内容 */}
                    <article className={styles.documentContent}>
                        {isMarkdownData ? (
                            // Markdown格式渲染
                            <MarkdownContentRenderer
                                markdownContent={contentList[0].content}
                                pageTitle={pageTitle}
                                approvalDate={approvalDate}
                                tocData={tocData}
                            />
                        ) : (
                            // 树形结构渲染
                            <section className={styles.section}>
                                <div className={styles.sectionTitle} ref={stickyTitleRef}>
                                    <span className={styles.titleText} data-title={pageTitle}>
                                        <span>{pageTitle}</span>
                                    </span>

                                    {approvalDate.content && (
                                        <span className={styles.dateText}>
                                            {approvalDate.key_desc}：{approvalDate.content}
                                        </span>
                                    )}
                                </div>
                                {renderTreeContent()}
                            </section>
                        )}
                    </article>

                {type === "drug" && tocData.length > 0 && (
                    <aside className={styles.tocSidebar}>
                        <h3 className={styles.tocTitle}>目录</h3>
                        <nav className={styles.tocList}>
                            <div className={styles.tocSection}>
                                <div className={styles.tocItems}>{renderTocItems(tocData)}</div>
                            </div>
                        </nav>
                    </aside>
                )}
                </div>
            </main>
        </div>
    );
};

export default DrugManualPreview;
